import React from 'react';
import { Badge } from '@atoms';

export interface UserRole {
  id: string;
  name: string;
  displayName: string;
  color: string | null;
}

export interface RoleBadgesProps {
  /** Array of user roles */
  roles: UserRole[];
  /** Maximum number of roles to display before showing "+X more" */
  maxDisplay?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * RoleBadges Molecule Component
 * 
 * Displays user roles as badges with optional color coding.
 * 
 * @example
 * ```tsx
 * <RoleBadges roles={user.roles} maxDisplay={3} />
 * ```
 */
export const RoleBadges: React.FC<RoleBadgesProps> = ({
  roles,
  maxDisplay = 3,
  className = '',
}) => {
  if (roles.length === 0) {
    return (
      <span className="text-sm text-text-secondary">No roles</span>
    );
  }

  const displayRoles = roles.slice(0, maxDisplay);
  const remainingCount = roles.length - maxDisplay;

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {displayRoles.map((role) => (
        <Badge
          key={role.id}
          variant="info"
          style={{
            backgroundColor: role.color ? `${role.color}20` : undefined,
            color: role.color || undefined
          }}
        >
          {role.displayName}
        </Badge>
      ))}
      {remainingCount > 0 && (
        <Badge variant="default">
          +{remainingCount} more
        </Badge>
      )}
    </div>
  );
};
